import { createSelector } from '@reduxjs/toolkit'
import reduce from 'lodash/reduce'
import find from 'lodash/find'
import defaultsDeep from 'lodash/defaultsDeep'
import filter from 'lodash/filter'
import { selectUsers } from 'selectors/users'
import { selectFocus, active } from 'selectors/graph'
import * as selectNodes from 'selectors/graph/nodes'
import * as selectEdges from 'selectors/graph/edges'
import { selectMe } from 'selectors/auth'

interface UserSummary {
  id: string
  [key: string]: unknown
}

interface EditableEntity {
  local?: boolean
  data?: {
    creator?: {
      id?: string
    }
    writers?: Array<{ id?: string }>
  }
}

interface NewContentState {
  readers: Record<string, boolean>
  writers: Record<string, boolean>
  expanded: boolean
}

interface PermissionsState {
  newContent?: NewContentState
  isVisible?: boolean
}

interface PermissionsRootState {
  permissions?: PermissionsState
  [key: string]: unknown
}

type UsersMap = Record<string, UserSummary>


const selectNewContent = (state: PermissionsRootState): NewContentState => state.permissions?.newContent || { readers: {}, writers: {}, expanded: false }

const selectPermissions = (state: PermissionsRootState): PermissionsState => state.permissions || { isVisible: false }

const defaultSelector = createSelector(selectNewContent, selectUsers, (newContent: NewContentState, users: unknown) => {
  const safeUsers = (users as UsersMap) || {}
  const readers = reduce(
    newContent?.readers || {},
    (rs: UsersMap, _: boolean, id: string) => {
      rs[id] = defaultsDeep(safeUsers[id], { id })
      return rs
    },
    {} as UsersMap
  )
  const writers = reduce(
    newContent?.writers || {},
    (ws: UsersMap, _: boolean, id: string) => {
      ws[id] = defaultsDeep(safeUsers[id], { id })
      return ws
    },
    {} as UsersMap
  )
  const expanded = Boolean(newContent?.expanded)
  return { readers, writers, expanded }
})
export const selectCanEditFocus = createSelector(selectFocus, selectNodes.all, selectEdges.all, selectMe, (focus: { id?: string }, ns: unknown, es: unknown, { me }: { me?: { id?: string } }) => {
  const safeNodes = (ns as Record<string, EditableEntity>) || {}
  const safeEdges = (es as Record<string, EditableEntity>) || {}
  const meId = me?.id
  if (!(focus?.id)) {
    return []
  }
  return filter([focus.id], (id: string) => {
    const o = safeNodes[id] || safeEdges[id]
    if (!o) {
      return false
    }
    if (o.local || o.data?.creator?.id === meId || find(o.data?.writers, (u: { id?: string }) => u.id === meId)) {
      return true
    }
    return false
  })
})

export const selectCanEditActiveNodes = createSelector(active.nodes, selectNodes.all, selectMe, (activeNodes: string[], ns: unknown, { me }: { me?: { id?: string } }) => {
  const safeNodes = (ns as Record<string, EditableEntity>) || {}
  const meId = me?.id
  return filter(activeNodes, (id: string) => {
    const o = safeNodes[id]
    if (!o) {
      return false
    }
    if (o.local || o.data?.creator?.id === meId || find(o.data?.writers, (u: { id?: string }) => u.id === meId)) {
      return true
    }
    return false
  })
})

export const selectCanEditActiveEdges = createSelector(active.edges, selectEdges.all, selectMe, (activeEdges: string[], es: unknown, { me }: { me?: { id?: string } }) => {
  const safeEdges = (es as Record<string, EditableEntity>) || {}
  const meId = me?.id
  return filter(activeEdges, (id: string) => {
    const o = safeEdges[id]
    if (!o) {
      return false
    }
    if (o.local || o.data?.creator?.id === meId || find(o.data?.writers, (u: { id?: string }) => u.id === meId)) {
      return true
    }
    return false
  })
})

export const selectCanEditActive = createSelector(selectCanEditActiveNodes, selectCanEditActiveEdges, (nIds: string[], eIds: string[]) => nIds.concat(eIds))

export const selectPermissionsVisible = createSelector(selectPermissions, (permissions: PermissionsState) => permissions.isVisible)

export default defaultSelector
